import React, { useCallback, useMemo } from 'react';

import { ServiceSegmentType } from '@mewa/types';
import {
  CollectionSlider,
  ProductCardGridItem,
  ResponsiveProductGrid,
  useSliderViewport,
} from '@mewa/ui';

import {
  PageFindResultData,
  ProductSearchResultMeta,
} from '../../components/search/types';
import { ProductCardColorSliderWrapper } from '../../components/slider/product-card-color-slider-wrapper';
import { ProductCardKeyFactsWrapper } from '../../components/slider/product-card-key-facts-wrapper';
import {
  productVariantsSeparator,
  propertiesSeparator,
} from '../product-details/meta-data';

export const SearchResultProducts = React.memo(
  ({
    results,
    langPrefix,
    expanded,
    detailsPageLabel,
  }: {
    results: PageFindResultData<ProductSearchResultMeta>[];
    langPrefix: string;
    expanded: boolean;
    detailsPageLabel: string;
  }) => {
    const sliderViewport = useSliderViewport();

    const renderProductSlides = useCallback(
      (product: PageFindResultData<ProductSearchResultMeta>, index: number) => {
        const isClothingCategory = JSON.parse(product.meta.product_is_clothing);

        const productData = {
          collection: {
            name: product.meta.product_collection,
            assets: {
              emotion: '',
            },
            claim: '',
            id: '',
            slug: '',
          },
          uuid: index.toString(),
          displayName: product.meta.product_title,
          keyFacts:
            product.meta.product_key_facts?.split(propertiesSeparator) ?? [],
          modelName: product.meta.product_title,
          serviceSegment: {
            key: ServiceSegmentType.businessClothing,
            slug: '',
          },
          slug: product.url,
          variants:
            product.meta.product_variants
              ?.split(productVariantsSeparator)
              .map((variant) => {
                const [assetId, colorName, hash, slug] =
                  variant.split(propertiesSeparator);

                return {
                  assetId,
                  colorName,
                  hash: hash,
                  name: '',
                  slug: slug,
                };
              }) ?? [],
        };

        return isClothingCategory ? (
          <ProductCardColorSliderWrapper
            key={productData.slug}
            useMbsFallbackIcon
            product={productData}
            langPrefix={langPrefix}
            detailsPageLabel={detailsPageLabel}
          />
        ) : (
          <ProductCardKeyFactsWrapper
            key={productData.slug}
            product={productData}
            useMbsFallbackIcon={false}
            langPrefix={langPrefix}
            detailsPageLabel={detailsPageLabel}
          />
        );
      },
      [langPrefix, detailsPageLabel],
    );

    const sliderView = useMemo(
      () => (
        <CollectionSlider
          slides={results}
          renderSlide={renderProductSlides}
          viewport={sliderViewport}
        />
      ),
      [results, renderProductSlides, sliderViewport],
    );

    const gridView = useMemo(
      () => (
        <ResponsiveProductGrid sx={{ marginTop: { xs: 0 } }}>
          {results.map((r, idx) => (
            <ProductCardGridItem key={idx}>
              {renderProductSlides(r, idx)}
            </ProductCardGridItem>
          ))}
        </ResponsiveProductGrid>
      ),
      [results, renderProductSlides],
    );

    return expanded ? gridView : sliderView;
  },
);
